import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useWorkspace } from '../../../utils/WorkspaceContext';
import api from '../../../services/api';

/** yyyy-mm-dd for a Date, in local time. */
const isoDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const STATUS_COLOR = {
  Draft: 'default',
  Submitted: 'info',
  Reviewed: 'warning',
  Finalized: 'success',
};

/** Colour an average against the 1–5 scale. */
const scoreColor = (value) => {
  if (value == null) return 'text.secondary';
  if (value >= 4.5) return 'success.main';
  if (value >= 3.5) return 'info.main';
  if (value >= 2.5) return 'warning.main';
  return 'error.main';
};

/**
 * Term/year performance rollup across many biweekly reviews.
 *
 * Averages each WQQI parameter over the chosen range so a term's worth of
 * fortnightly reviews reads as one picture, and shows self-assessment beside
 * supervisor assessment so the gap between them is visible. Employees see only
 * their own history; supervisors can scope to anyone.
 */
const PerformanceSummary = () => {
  const { currentWorkspace } = useWorkspace();

  // Default to the trailing six months — roughly one term.
  const [start, setStart] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return isoDate(date);
  });
  const [end, setEnd] = useState(() => isoDate(new Date()));
  const [employeeId, setEmployeeId] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    api
      .get('assignees/', { params: { workspace_id: currentWorkspace.id } })
      .then(({ data }) => setAssignees(Array.isArray(data) ? data : data?.payload || []))
      .catch(() => setAssignees([]));
  }, [currentWorkspace]);

  const load = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('biweekly-reports/performance-summary/', {
        params: {
          workspace_id: currentWorkspace.id,
          start,
          end,
          ...(employeeId ? { employee: employeeId } : {}),
        },
      });
      setSummary(data.payload);
    } catch {
      setSummary(null);
      setError('Could not load the performance summary.');
    }
    setLoading(false);
  }, [currentWorkspace, start, end, employeeId]);

  useEffect(() => { load(); }, [load]);

  if (!currentWorkspace?.id) {
    return <Alert severity="info" sx={{ m: 2 }}>Pick a workspace to see performance data.</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Performance Summary</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Biweekly reviews aggregated across a term or year.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          type="date" size="small" label="From" value={start}
          onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date" size="small" label="To" value={end}
          onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <TextField
          select size="small" label="Employee" value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)} sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Everyone I can see</MenuItem>
          {assignees.map((person) => (
            <MenuItem key={person.id} value={String(person.id)}>
              {person.full_name || person.username}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && summary && (
        summary.report_count === 0 ? (
          <Alert severity="info">No biweekly reviews in this range.</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`${summary.report_count} review(s)`} size="small" />
              <Chip label={`${summary.employee_count} employee(s)`} size="small" />
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 7 }}>
                <Paper variant="outlined">
                  <Typography variant="subtitle2" sx={{ p: 1.5 }}>
                    Average rating by parameter
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Dimension</TableCell>
                        <TableCell>Parameter</TableCell>
                        <TableCell align="right">Self</TableCell>
                        <TableCell align="right">Supervisor</TableCell>
                        <TableCell align="right">Rated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.parameters.map((row) => (
                        <TableRow key={row.parameter_id}>
                          <TableCell>{row.dimension}</TableCell>
                          <TableCell>{row.parameter}</TableCell>
                          <TableCell align="right" sx={{ color: scoreColor(row.employee_average) }}>
                            {row.employee_average ?? '—'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: scoreColor(row.supervisor_average), fontWeight: 600 }}
                          >
                            {row.supervisor_average ?? '—'}
                          </TableCell>
                          <TableCell align="right">{row.rated_periods}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, lg: 5 }}>
                <Paper variant="outlined">
                  <Typography variant="subtitle2" sx={{ p: 1.5 }}>
                    Period by period
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Avg</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.trend.map((row) => (
                        <TableRow key={row.report_id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {row.period_start} – {row.period_end}
                          </TableCell>
                          <TableCell>{row.employee}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.status}
                              color={STATUS_COLOR[row.status] || 'default'}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: scoreColor(row.supervisor_average), fontWeight: 600 }}
                          >
                            {row.supervisor_average ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            </Grid>
          </>
        )
      )}
    </Box>
  );
};

export default PerformanceSummary;
